import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Simple GA4 data fetcher — replaces the repeated useState/useCallback/useEffect triplet.
 *
 * Stale-while-revalidate: when args change (date range, client) and we already have data,
 * the previous data stays visible during the refetch. `loading` only flips true when
 * there's nothing to show yet, so consumers using `if (loading) return skeleton` get the
 * skeleton on first load but no flash on subsequent refetches.
 *
 * @param {Function} apiFn   – async function that returns { data }
 * @param {Array}    args    – arguments forwarded to apiFn (also used as deps)
 * @param {Object}   [opts]
 * @param {boolean}  [opts.skip]       – skip fetching when true
 * @param {Function} [opts.transform]  – transform resp before storing (receives full response)
 * @returns {{ data, loading, error, isRevalidating }}
 */
const useGA4Fetch = (apiFn, args = [], { skip = false, transform } = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isRevalidating, setIsRevalidating] = useState(false);
  const dataRef = useRef(null);
  // Monotonic request id. Incremented on every load(); a response whose id is no
  // longer the latest is discarded. Without this, switching date range A → B and
  // having A resolve second leaves the UI showing A's rows under B's filters.
  // We can't use an `AbortController` here because apiFn is an axios wrapper that
  // doesn't take a signal, so we drop the stale result instead of the request.
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    if (skip) return;
    const myReq = ++reqIdRef.current;
    if (dataRef.current == null) setLoading(true);
    setIsRevalidating(true);
    try {
      const resp = await apiFn(...args);
      if (myReq !== reqIdRef.current) return;
      const next = transform ? transform(resp) : (resp?.data ?? null);
      dataRef.current = next;
      setData(next);
      setError('');
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      // Keep stale data on error rather than wiping it; only surface an error
      // (for the consumer's error branch) when there's nothing to show.
      if (dataRef.current == null) {
        setData(null);
        setError(e?.message || 'Failed to load data');
      }
    } finally {
      // Only the newest request may clear the busy flags — otherwise a slow
      // loser resolving after the winner would flip loading off while the
      // winner is still in flight.
      if (myReq === reqIdRef.current) {
        setLoading(false);
        setIsRevalidating(false);
      }
    }
  }, [skip, ...args]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, isRevalidating };
};

export default useGA4Fetch;
